import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`from{opacity:0;transform:translateY(15px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height:100vh;background:#FFFDF7;
  padding:40px 20px 60px;
`;
const Container = styled.div`max-width:1100px;margin:0 auto;`;
const Title = styled.h1`
  text-align:center;font-family:'Playfair Display',Georgia,serif;color:#2C1810;font-size:2.2rem;margin-bottom:8px;
`;
const Subtitle = styled.p`text-align:center;color:#9B8B7A;font-size:1rem;margin-bottom:40px;`;

const Section = styled.div`margin-bottom:36px;animation:${fadeIn} 0.4s ease ${p=>p.delay||'0s'} both;`;
const SectionTitle = styled.h2`
  color:#8B6914;font-size:1.1rem;text-transform:uppercase;letter-spacing:2px;
  margin-bottom:16px;padding-left:4px;font-family:'Playfair Display',Georgia,serif;
  &::before{content:'';display:inline-block;width:4px;height:18px;background:${p => p.theme.colors.bands.exceptional};border-radius:2px;margin-right:10px;vertical-align:middle;}
`;

const Grid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:16px;
  @media(max-width:600px){grid-template-columns:1fr 1fr}
  @media(max-width:400px){grid-template-columns:1fr}
`;

const ToolCard = styled.div`
  background:${p => p.theme.colors.surface};border:1px solid rgba(218,165,32,0.1);
  border-radius: ${p => p.theme.radius.lg}px;padding:24px 20px;cursor:pointer;
  transition:all 0.25s ease;position:relative;overflow:hidden;
  box-shadow:0 2px 8px rgba(139,105,20,0.04);
  &:hover{border-color:${p=>p.glow||p.theme.colors.bands.exceptional};
    transform:translateY(-3px);box-shadow:0 8px 25px rgba(139,105,20,0.1);}
`;
const ToolIcon = styled.div`font-size:2rem;margin-bottom:10px;`;
const ToolName = styled.div`color:#2C1810;font-weight:700;font-size:1rem;margin-bottom:6px;`;
const ToolDesc = styled.div`color:#9B8B7A;font-size:0.8rem;line-height:1.4;`;
const Badge = styled.span`
  position:absolute;top:12px;right:12px;
  background:${p=>p.bg||p.theme.colors.bands.exceptional};color:${p => p.theme.colors.surface};font-size:0.65rem;font-weight:700;
  padding:3px 8px;border-radius:10px;text-transform:uppercase;letter-spacing:0.5px;
`;

const BackBtn = styled.button`
  background:none;border:1px solid rgba(218,165,32,0.25);color:#8B6914;
  padding:8px 20px;border-radius: ${p => p.theme.radius.md}px;cursor:pointer;font-size:0.85rem;margin-bottom:24px;
  &:hover{border-color:${p => p.theme.colors.bands.exceptional};color:${p => p.theme.colors.bands.exceptional};background:rgba(218,165,32,0.05)}
`;

const tools = {
  'Birth Chart': [
    { icon: '📜', name: 'Free Horoscope', desc: 'Full birth chart with planets, dashas, yogas & AV analysis', path: '/free-kundli', glow: '#f7971e', badge: 'Free' },
    { icon: '⚡', name: 'KalaShakti Daily Balance', desc: 'Personalized 0-100 daily score with hourly & weekly forecast', path: '/daily-balance', glow: '#9c27b0', badge: 'Free' },
    { icon: '🔱', name: 'KalaSakthi V2', desc: 'Life blueprint — 12 house scores, 5-category radar & drill-down', path: '/kalasakthi', glow: '#e91e63', badge: 'Free' },
  ],
  'Calendar & Timing': [
    { icon: '🕉️', name: 'Today Panchangam', desc: 'Tithi, Nakshatra, Yoga, Karana & Vara for today', path: '/today-panchangam', glow: '#4caf50', badge: 'Free' },
    { icon: '🕐', name: 'Daily Timings', desc: 'Rahu Kalam, Yamagandam, Gulika & Choghadiya (சோகடியா)', path: '/daily-timings', glow: '#e65100', badge: 'Free' },
    { icon: '🪐', name: 'Planetary Hora', desc: '24 planetary hours — know which planet rules each hour', path: '/hora', glow: '#1565c0', badge: 'Free' },
    { icon: '✨', name: 'Abhijit Muhurta', desc: 'The most auspicious 48-minute golden window at noon', path: '/abhijit-muhurta', glow: '#ffc107', badge: 'Free' },
    { icon: '📅', name: 'Muhurta Checker', desc: 'Score any date for marriage, travel, business & more', path: '/muhurta-check', glow: '#2e7d32', badge: 'Free' },
  ],
  'Marriage Matching': [
    { icon: '💍', name: 'Horoscope Matching', desc: 'Ashtakoot gun milan — 8 kootas, 36-point North Indian system', path: '/kundli-matching', glow: '#e91e63', badge: 'Free' },
    { icon: '💑', name: '10 Porutham', desc: 'Tamil marriage compatibility — Rajju, Vedha, Nadi & more', path: '/porutham', glow: '#ad1457', badge: 'Free' },
  ],
  'KP Stellar System': [
    { icon: '🎯', name: 'KP Chart', desc: 'Krishnamurti Paddhati — Sub-Lord analysis, Placidus houses, significators', path: '/kp-chart', glow: '#00bcd4', badge: 'Free' },
  ],
  'Divination & Prashnavali': [
    { icon: '🏹', name: 'Rama Shalaka', desc: 'Ask Sri Rama a question — 9×9 divination grid from Ramacharitmanas', path: '/rama-shalaka', glow: '#DAA520' },
    { icon: '🕉️', name: 'Ganesha Prashnavali', desc: 'Ask Ganapathi for guidance — 8×8 oracle grid with auspiciousness reading', path: '/ganesha-prashnavali', glow: '#EF6C00' },
    { icon: '🙏', name: 'Hanuman Prashnavali', desc: "Seek Hanuman's counsel for courage — 7×7 path-trace from the Hanuman Chalisa", path: '/hanuman-prashnavali', glow: '#C62828' },
    { icon: '📜', name: 'Chanakya Niti', desc: "Consult Chanakya's aphorisms on strategy and ethics — 6×6 Niti Shastra grid", path: '/chanakya-niti', glow: '#303F9F' },
  ],
  'Coming Soon': [
    { icon: '🔮', name: 'Dasha Interpretation', desc: 'Current running dasha period with life predictions', path: '#', glow: '#555', badge: 'Soon' },
    { icon: '📊', name: 'Event Timing', desc: 'Best timing windows for marriage, career, property', path: '#', glow: '#555', badge: 'Soon' },
    { icon: '🌟', name: 'Yoga Analysis', desc: 'Detect Raja, Dhan & other yogas in your chart', path: '#', glow: '#555', badge: 'Soon' },
    { icon: '🌍', name: 'Transit Analysis', desc: 'Current transit impact on your birth chart', path: '#', glow: '#555', badge: 'Soon' },
    { icon: '⭐', name: 'Tara Bala', desc: 'Daily star compatibility for activity planning', path: '#', glow: '#555', badge: 'Soon' },
  ],
};

const VedicTools = () => {
  const navigate = useNavigate();
  const sections = Object.entries(tools);

  return (
    <Page>
      <Container>
        <BackBtn onClick={() => navigate('/')}>Back to Home</BackBtn>
        <Title>Vedic Astrology Tools</Title>
        <Subtitle>Powered by Vedic Astrology</Subtitle>

        {sections.map(([category, items], si) => (
          <Section key={category} delay={`${si * 0.1}s`}>
            <SectionTitle>{category}</SectionTitle>
            <Grid>
              {items.map((tool) => (
                <ToolCard
                  key={tool.name}
                  glow={tool.glow}
                  onClick={() => tool.path !== '#' && navigate(tool.path)}
                  style={tool.path === '#' ? { opacity: 0.5, cursor: 'default' } : {}}
                >
                  {tool.badge && <Badge bg={tool.glow}>{tool.badge}</Badge>}
                  <ToolIcon>{tool.icon}</ToolIcon>
                  <ToolName>{tool.name}</ToolName>
                  <ToolDesc>{tool.desc}</ToolDesc>
                </ToolCard>
              ))}
            </Grid>
          </Section>
        ))}
      </Container>
    </Page>
  );
};

export default VedicTools;
